import { useMemo, memo, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormikErrors } from 'formik';
import { Divider, Stack } from '@mui/material';

import { Dropzone } from 'features/file-loader';

import type {
  TaskTodo,
  TaskTodoCreateOptions,
  Option,
  EntityRef,
  InitTodoForm,
} from 'models';
import { toDate } from 'utils';

import ControlLabel from 'shared/components/ControlLabel';
import Textarea from 'shared/components/Textarea';
import {
  PageLoader,
  Error,
  ValidationWarning,
  Input,
  DatePicker,
  NoData,
  SaveConfirm,
  Modal,
  Select,
} from 'components';
import ExpandedForm from '../ExpandedForm';

import {
  useStyles,
  StyledIconButton,
  StyledTitle,
  SaveButton,
  CloseButton,
  ButtonLoader,
} from '../style';
import ExpandIcon from 'shared/icons/Expand';

interface Props {
  files: File[];
  upload: boolean;
  options: TaskTodoCreateOptions | null;
  loading: boolean;
  error: boolean;
  values: InitTodoForm;
  validationErrors: FormikErrors<TaskTodo>;
  confirmationWarning: boolean;
  saveAndCopyUpload: boolean;
  handleCloseTask: () => void;
  closeWithoutSaving: () => void;
  closeWithSaving: () => void;
  handleInitErrors: () => void;
  handleChangeDate: (date: Date | null) => void;
  handleChangeInput: (e: ChangeEvent<HTMLInputElement>) => void;
  handleChangeSelect: (value: Option | EntityRef, name?: string) => void;
  handleChangeTextArea: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  handleChangeFiles: (files: File[]) => void;
  handleSaveAndCopy: () => void;
  onChangeDescription: (value: string) => void;
  onChangeResolutionDescription: (value: string) => void;
}

const TodoCreateForm: React.FC<Props> = ({
  loading,
  error,
  files,
  confirmationWarning,
  validationErrors,
  values,
  options,
  upload,
  handleChangeInput,
  handleChangeDate,
  handleChangeSelect,
  handleChangeTextArea,
  handleInitErrors,
  handleChangeFiles,
  closeWithSaving,
  closeWithoutSaving,
  handleCloseTask,
  handleSaveAndCopy,
  saveAndCopyUpload,
  onChangeDescription,
  onChangeResolutionDescription,
}) => {
  const [isShowedExpandedDescription, setIsShowedExpandedDescription] =
    useState(false);
  const [
    isShowedExpandedResolutionDescription,
    setIsShowedExpandedResolutionDescription,
  ] = useState(false);

  const classes = useStyles();
  const { t } = useTranslation();

  const handleOpenExpandedDescription = () => {
    setIsShowedExpandedDescription(true);
  };
  const handleCloseExpandedDescription = () => {
    setIsShowedExpandedDescription(false);
  };
  const handleOpenExpandedResolutionDescription = () => {
    setIsShowedExpandedResolutionDescription(true);
  };
  const handleCloseExpandedResolutionDescription = () => {
    setIsShowedExpandedResolutionDescription(false);
  };

  const selectCls = useMemo(
    () => ({
      root: classes.rootSelect,
      paper: {
        root: classes.paper,
      },
      select: classes.select,
      btn: {
        root: classes.selectBtn,
      },
    }),
    [classes]
  );

  if (loading) return <PageLoader />;
  if (error) return <Error />;
  if (!options) return <NoData />;

  const disabled = upload || saveAndCopyUpload;

  return (
    <>
      <Stack spacing={3}>
        <StyledTitle>Uusi tehtävä</StyledTitle>
        <Stack
          direction={{
            md: 'row',
            xs: 'column',
          }}
          component="form"
          divider={<Divider orientation="vertical" flexItem />}
          spacing={2.5}
        >
          <Stack spacing={2.5} flex={1.5}>
            <Stack spacing={1}>
              <ControlLabel required>
                {t('#portal.task.todo.form.control.label.subject')}
              </ControlLabel>
              <ValidationWarning
                handleInitErrors={handleInitErrors}
                text={validationErrors.Subject}
                Component={
                  <Input
                    value={values.Subject}
                    name="Subject"
                    onChange={handleChangeInput}
                    disabled={disabled}
                  />
                }
              />
            </Stack>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <ControlLabel>
                  {t('#portal.task.todo.form.control.label.description')}
                </ControlLabel>
                <StyledIconButton onClick={handleOpenExpandedDescription}>
                  <ExpandIcon sx={{ color: '#224060', fontSize: 18 }} />
                </StyledIconButton>
              </Stack>
              <ValidationWarning
                text={validationErrors.Description}
                handleInitErrors={handleInitErrors}
                Component={
                  <Textarea
                    value={values.Description || ''}
                    name="Description"
                    placeholder="Kirjoita kuvaus tehtävästä."
                    onChange={handleChangeTextArea}
                    className={classes.textArea}
                    disabled={disabled}
                  />
                }
              />
            </Stack>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <ControlLabel>
                  {t(
                    '#portal.task.todo.form.control.label.resolutiondescription'
                  )}
                </ControlLabel>
                <StyledIconButton
                  onClick={handleOpenExpandedResolutionDescription}
                >
                  <ExpandIcon sx={{ color: '#224060', fontSize: 18 }} />
                </StyledIconButton>
              </Stack>
              <ValidationWarning
                text={validationErrors.ResolutionDescription}
                handleInitErrors={handleInitErrors}
                Component={
                  <Textarea
                    value={values.ResolutionDescription || ''}
                    name="ResolutionDescription"
                    placeholder="Kirjoita kuvaus ratkaisusta."
                    onChange={handleChangeTextArea}
                    className={classes.textArea}
                    disabled={disabled}
                  />
                }
              />
            </Stack>
          </Stack>
          <Stack spacing={4} flex={1}>
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              spacing={2}
            >
              <Stack spacing={1}>
                <ControlLabel>
                  {t('#portal.task.todo.form.control.label.startdate')}
                </ControlLabel>
                <DatePicker
                  date={toDate(values.StartDate)}
                  disabled={disabled}
                  maxDate={toDate(values.DueDate)}
                  name="StartDate"
                  handleChange={handleChangeDate}
                  variant="underline"
                />
              </Stack>
              <Stack spacing={1}>
                <ControlLabel required>
                  {t('#portal.task.todo.form.control.label.duedate')}
                </ControlLabel>
                <ValidationWarning
                  text={validationErrors.DueDate}
                  handleInitErrors={handleInitErrors}
                  Component={
                    <DatePicker
                      date={toDate(values.DueDate)}
                      disabled={disabled}
                      minDate={toDate(values.StartDate)}
                      name="DueDate"
                      handleChange={handleChangeDate}
                      variant="underline"
                    />
                  }
                />
              </Stack>
            </Stack>

            <Stack spacing={1}>
              <ControlLabel>
                {t('#portal.task.todo.form.control.label.priority')}
              </ControlLabel>
              <Select
                value={values.Priority}
                options={options.Priority}
                name="Priority"
                onSelect={handleChangeSelect}
                labelField="Label"
                valueField="Value"
                cls={selectCls}
                parentWidth
                disabled={disabled}
              />
            </Stack>
            <Stack spacing={1}>
              <ControlLabel required>
                {t('#portal.task.todo.form.control.label.executor')}
              </ControlLabel>
              <ValidationWarning
                text={validationErrors.Executor}
                handleInitErrors={handleInitErrors}
                Component={
                  <Select
                    value={values.Executor}
                    options={options.Executor}
                    name="Executor"
                    onSelect={handleChangeSelect}
                    labelField="Name"
                    valueField="Id"
                    cls={selectCls}
                    parentWidth
                    disabled={disabled}
                  />
                }
              />
            </Stack>
            <Stack spacing={1}>
              <ControlLabel>
                {t('#portal.task.todo.form.control.label.status')}
              </ControlLabel>
              <Select
                value={values.Status}
                options={options.Status}
                name="Status"
                onSelect={handleChangeSelect}
                labelField="Label"
                valueField="Value"
                cls={selectCls}
                parentWidth
                disabled={disabled}
              />
            </Stack>
            <Dropzone
              value={files}
              disabled={disabled}
              onChange={handleChangeFiles}
            />
          </Stack>
        </Stack>
        <Stack
          direction="row"
          spacing={{
            sm: 2,
            xs: 1,
          }}
        >
          <SaveButton onClick={closeWithSaving} disabled={disabled}>
            {t('#portal.task.todo.form.button.label.save')}
            {upload && <ButtonLoader />}
          </SaveButton>
          <SaveButton onClick={handleSaveAndCopy} disabled={disabled}>
            Tallenna ja kopioi
            {saveAndCopyUpload && <ButtonLoader />}
          </SaveButton>
          <CloseButton onClick={handleCloseTask} disabled={disabled}>
            {t('#portal.task.todo.form.button.label.close')}
          </CloseButton>
        </Stack>
      </Stack>
      <SaveConfirm
        open={confirmationWarning}
        apply={closeWithSaving}
        cancel={closeWithoutSaving}
      />
      <Modal
        fullHeight
        fullWidth
        maxWidth="sm"
        open={isShowedExpandedDescription}
        handleClose={handleCloseExpandedDescription}
      >
        <ExpandedForm
          label={`Uusi tehtävä - ${t(
            '#portal.task.todo.form.control.label.description'
          )}`}
          placeholder="Kirjoita kuvaus tehtävästä."
          value={values.Description || ''}
          onChange={onChangeDescription}
          onClose={handleCloseExpandedDescription}
        />
      </Modal>
      <Modal
        fullHeight
        fullWidth
        maxWidth="sm"
        open={isShowedExpandedResolutionDescription}
        handleClose={handleCloseExpandedResolutionDescription}
      >
        <ExpandedForm
          label={`Uusi tehtävä - ${t(
            '#portal.task.todo.form.control.label.resolutiondescription'
          )}`}
          placeholder="Kirjoita kuvaus ratkaisusta."
          value={values.ResolutionDescription || ''}
          onChange={onChangeResolutionDescription}
          onClose={handleCloseExpandedResolutionDescription}
        />
      </Modal>
    </>
  );
};

export default memo(TodoCreateForm);
